import React, { useEffect } from "react";
import { X } from "lucide-react";

export interface AuditSidePanelProps {
    open: boolean;
    onClose: () => void;
    title: string;
    subtitle?: React.ReactNode;
    /** header right-side actions (e.g. refresh) */
    actions?: React.ReactNode;
    /** tailwind max-width on >= sm screens; full-screen below */
    widthClass?: string;
    zIndexClass?: string;
    children: React.ReactNode;
    /** sticky area under the header (filters) */
    toolbar?: React.ReactNode;
    footer?: React.ReactNode;
    /** let a stacked panel own the Escape key */
    disableEscape?: boolean;
}

/** Right-side panel on desktop, full-screen sheet on mobile. */
export const AuditSidePanel: React.FC<AuditSidePanelProps> = ({
    open,
    onClose,
    title,
    subtitle,
    actions,
    widthClass = "sm:max-w-[440px]",
    zIndexClass = "z-[1600]",
    children,
    toolbar,
    footer,
    disableEscape = false,
}) => {
    useEffect(() => {
        if (!open || disableEscape) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.stopPropagation();
                onClose();
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose, disableEscape]);

    if (!open) return null;

    return (
        <div className={`fixed inset-0 ${zIndexClass} flex justify-end bg-black/40`} onMouseDown={onClose}>
            <aside
                role="dialog"
                aria-modal="true"
                aria-label={title}
                onMouseDown={(e) => e.stopPropagation()}
                className={`flex h-full w-full flex-col bg-white shadow-2xl ${widthClass}`}
            >
                <header className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3">
                    <div className="min-w-0">
                        <h3 className="truncate text-base font-semibold text-gray-900">{title}</h3>
                        {subtitle && <div className="mt-0.5 truncate text-xs text-gray-500">{subtitle}</div>}
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-1">
                        {actions}
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100"
                            aria-label={`Close ${title}`}
                        >
                            <X size={18} />
                        </button>
                    </div>
                </header>
                {toolbar && <div className="border-b border-gray-200 bg-gray-50/60 px-4 py-3">{toolbar}</div>}
                <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{children}</div>
                {footer && <div className="border-t border-gray-200 px-4 py-2.5">{footer}</div>}
            </aside>
        </div>
    );
};

export default AuditSidePanel;
